import {useContext, useEffect, useRef, useState} from "react";
import AppContext from "../AppContext";
import './SimBTH.scss'
import Header from "../components/sim/unified/Header";
import QuestionGrid from "../components/sim/shared/QuestionGrid";
import Footer from "../components/sim/unified/Footer";
import Page1 from "../components/sim/unified/Page1";
import Page2 from "../components/sim/unified/Page2";
import Page3 from "../components/sim/unified/Page3";
import Page4 from "../components/sim/unified/Page4";
import Page5 from "../components/sim/unified/Page5";
import API, {HandleError} from "../API";
import {useSnackbar} from "notistack";
import {useNavigate} from "react-router-dom";
import {User} from "../models/User";
import {isCorrectAnswer} from "../Tools";

export default function SimUnified() {
    const app = useContext(AppContext)
    const { enqueueSnackbar } = useSnackbar()
    const navigate = useNavigate()
    useEffect(() => {
        document.body.classList.add("sim-page");
        return () => {
            document.body.classList.remove("sim-page");
        };
    }, []);

    const [sim, setSim] = useState({
        currentStep: 1,
        currentTime: 0,
        currentQuestions: [],
        currentQuestion: 0,
        currentTimeLabel: '',
        maxTime: 0,
        timeRunning: false,
        gridVisible: false,
        finished: false,
        startDate: null,
        totalImages: [],
        imagesLoaded: 0,
        questions: [],
        token: '',
        submitted: false
    })

    useEffect(() => {
        async function fetchQuestions() {
            app.startLoading()
            try {
                const res = await API({
                    method: 'GET',
                    url: `/simulation/${app.user?.current_course}`,
                    headers: {
                        'Authorization': `Bearer ${app.token}`,
                    },
                    data: {}
                })

                if (res.status < 200 || res.status > 299)
                    throw res.data;

                app.stopLoading()

                let images = []
                res.data.questions.forEach((q, idx) => {
                    if(q.image_uri) {
                        images.push(q.image_uri);
                    }
                })
                setSim({...sim, questions: [...res.data.questions], totalImages: images})
            }
            catch(e) {
                app.stopLoading()
                HandleError(e, 'Error', enqueueSnackbar, app.logout);
            }
        }
        fetchQuestions()
        window.loadedImages = 0
        window.loadingImages = false;
    }, [])
    //First quiz Timer
    const quizTimer = useRef(null)
    useEffect(() => {
        if(sim.timeRunning) {
            var t = sim.currentTime
            if(sim.startDate !== null)
                t = sim.maxTime - Math.floor(((new Date()).getTime() - sim.startDate.getTime()) / 1000)
            setSim({...sim, currentTimeLabel: t })

            quizTimer.current = setTimeout(() => {
                if(sim.currentTime <= 0) {
                    goNext()
                }
                else {
                    if(sim.startDate !== null)
                        t = sim.maxTime - Math.floor(((new Date()).getTime() - sim.startDate.getTime()) / 1000)
                    else
                        t = sim.currentTime - 1

                    setSim({...sim, currentTime: t, currentTimeLabel: t })
                }
            }, 1000)
            return () => clearTimeout(quizTimer.current)
        }
    }, [sim.currentQuestion, sim.gridVisible, sim.currentStep, sim.currentQuestions, sim.timeRunning, sim.currentTime])

    useEffect(() => {
        getToken()
    }, [])

    useEffect(() => {
        if(sim.currentStep === 4 && !sim.submitted)
            postResult()
    }, [sim.currentStep])

    const getToken = async () => {
        app.startLoading()
        try {
            const res = await API({
                method: 'GET',
                url: '/points-token/sim',
                headers: {
                    'Authorization': `Bearer ${app.token}`,
                }
            })

            if (res.status < 200 || res.status > 299)
                throw res.data;

            app.stopLoading()
            window.simToken = res.data.token
            setSim({...sim, token: res.data.token})
        }
        catch(e) {
            app.stopLoading()
            HandleError(e, 'Error', enqueueSnackbar, app.logout);
        }
    }

    const postResult = async () => {
        const score = getResult()
        if(!score.result)
            return


        app.startLoading()
        try {
            const res = await API({
                method: 'POST',
                url: '/points',
                headers: {
                    'Authorization': `Bearer ${app.token}`,
                },
                data: {
                    token: window.simToken,
                    event: 'simulation_completed',
                    perfect: score.correct == score.total ? 1 : 0,
                    ref_id: -100,
                    parent_id: app.user?.current_course
                }
            })

            if (res.status < 200 || res.status > 299)
                throw res.data;

            app.stopLoading()
            setSim({...sim, submitted: true})
            app.setUser(new User(res.data.me))
            enqueueSnackbar(`Je hebt ${res.data.points} punten verdiend!`, {variant: 'success', style: {whiteSpace: 'pre-line'}});
        }
        catch(e) {
            app.stopLoading()
            HandleError(e, 'Error', enqueueSnackbar, app.logout);
        }
    }

    const setQuestion = (index, newQuestion) => {
        if(sim.currentQuestions.length) {
            const q = sim.currentQuestions?.[index]
            if(q) {
                const questions = [...sim.currentQuestions]
                questions[index] = {...newQuestion}
                setSim({...sim, currentQuestions: questions})
            }
        }
    }

    const getQuestion = () => {
        if(sim.currentQuestions.length) {
            const q = sim.currentQuestions?.[sim.currentQuestion]
            if(q)
                return q
        }
        return null
    }

    const goNext = () => {
        //console.log(window.simToken);
        if(sim.finished) {
            switch(sim.currentStep) {
                case 2:
                    if(sim.currentQuestion < sim.currentQuestions.length - 1)
                        setSim({...sim, currentQuestion: sim.currentQuestion + 1 })
                    else
                        setSim({...sim, currentStep: 5, currentQuestion: 0 })
                    break
                case 4:
                    setSim({...sim, currentStep: 5, startDate: null, finished: true, maxTime: 0, currentTime: 0, gridVisible: false, currentQuestion: 0, timeRunning: false })
                    break
                case 5:
                    navigate('/')
                    break
                default:
                    return
            }
            return
        }
        switch(sim.currentStep) {
            case 1:
                setSim({...sim, currentStep: 2, currentTime: 30 * 60, maxTime: 30 * 60, startDate: new Date(), timeRunning: true, currentQuestion: 0, currentQuestions: [...sim.questions] })
                break;
            case 2:
                if(sim.currentTime <= 0) {
                    setSim({...sim, currentStep: 4, startDate: null, maxTime: 0, timeRunning: false, gridVisible: false })
                }
                else {
                    if(sim.currentQuestion < sim.currentQuestions.length - 1) {
                        const questions = [...sim.currentQuestions]
                        const q = questions[sim.currentQuestion]
                        q.is_correct = isCorrectAnswer(q)
                        setQuestion(sim.currentQuestion, q)
                        setSim({...sim, currentQuestion: sim.currentQuestion + 1, timeRunning: true, gridVisible: false })
                    }
                    else {
                        setSim({...sim, currentStep: 3, gridVisible: false, currentQuestion: sim.currentQuestions.length - 1 })
                    }
                }
                break;
            case 3:
                if(sim.currentTime <= 0) {
                    setSim({...sim, currentStep: 4, startDate: null, maxTime: 0, timeRunning: false, gridVisible: false })
                }
                setSim({...sim, currentStep: 4, currentTime: 0, currentQuestion: 0, timeRunning: false })
                break
            case 4:
                setSim({...sim, currentStep: 5, startDate: null, finished: true, maxTime: 0, currentTime: 0, gridVisible: false, currentQuestion: 0, timeRunning: false })
                break
            case 5:
                navigate('/')
                break
            default:
                return
        }
    }
    const getResult = () => {
        let correct = 0
        let required_score = 50
        switch(app.user?.current_course) {
            case 1:
                required_score = 44
                break
            case 2:
                required_score = 41
                break
            case 3:
                required_score = 43
                break
        }
        sim.currentQuestions.forEach((q, idx) => {
            if(isCorrectAnswer(q))
                correct++
        });
        return {
            result: correct >= required_score,
            min: required_score,
            correct: correct,
            total: sim.currentQuestions.length
        }
    }

    const toggleGrid = () => {
        setSim({ ...sim, gridVisible: !sim.gridVisible })
    }
    const goToQuestion = (idx, step) => {
        if(sim.finished) {
            const newData = { currentStep: step, currentQuestion: idx, gridVisible: false }
            setSim({...sim, ...newData})
            return
        }
        if(sim.timeRunning && sim.currentQuestions.length && (idx >= 0 && idx < sim.currentQuestions.length)) {
            const newData = { currentQuestion: idx, gridVisible: false }
            if (sim.currentStep === 3)
                newData['currentStep'] = 2;

            setSim({...sim, ...newData })
        }
    }
    const renderStep = () => {
        switch(sim.currentStep) {
            case 1:
                return <Page1 sim={sim} setSim={setSim} />
            case 2:
                return <Page2 sim={sim} setSim={setSim} setQuestion={setQuestion} />
            case 3:
                return <Page3 sim={sim} setSim={setSim} goNext={goNext} goToQuestion={goToQuestion} />
            case 4:
                return <Page4 getResult={getResult} />
            case 5:
                return <Page5 sim={sim} setSim={setSim} getResult={getResult} goToQuestion={goToQuestion} />
            default:
                return null
        }
    }
    return <div className={"sim-exam-app " + (sim.finished ? 'finished' : '')}>
        <Header sim={sim} setSim={setSim} toggleGrid={toggleGrid} setQuestion={setQuestion} getQuestion={getQuestion} />
        <div className="exam-sim-page">
            {renderStep()}
            <QuestionGrid sim={sim} setSim={setSim} goToQuestion={goToQuestion} toggleGrid={toggleGrid} />
        </div>
        <Footer sim={sim} setSim={setSim} goNext={goNext} goToQuestion={goToQuestion} />
    </div>
}